import React, { useEffect, useMemo, useRef, useState } from 'react';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Center from '../../components/css/Center';
import SameLine from '../../components/css/SameLine';
import Button from '../../components/elements/Button';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { fetchUserInfo, apiRequest } from '../../utils';

const BonusPoints = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [bumping, setBumping] = useState(false);
  const [floats, setFloats] = useState([]);
  const [pendingKey, setPendingKey] = useState(null);
  const totalRef = useRef(null);

  useEffect(() => {
    fetchUserInfo('/bonus_points', setInfo, setLoading, setError);
    fetchUserInfo('/team_readiness?own_team=1', (data) => setReadiness(data.readiness), setLoading, setError);
  }, []);

  const checks = readiness?.checks || [];

  const ICON_BY_KEY = {
    captain: 'crown',
    players: 'users',
    photo: 'camera',
    slogan: 'comment-quote',
    bio: 'book-alt',
    song: 'music',
    all_player_photos: 'portrait',
    joker: 'star',
    challenged: 'boxing-glove',
    mission: 'flag',
  };

  const triggerBump = () => {
    setBumping(false);
    requestAnimationFrame(() => setBumping(true));
    setTimeout(() => setBumping(false), 650);
    const id = Date.now() + Math.random();
    setFloats((prev) => [...prev, id]);
    setTimeout(() => setFloats((prev) => prev.filter((f) => f !== id)), 950);
  };

  const claim = async (key) => {
    if (pendingKey) return;
    setPendingKey(key);
    try {
      const res = await apiRequest('POST', '/claim_readiness_bonus', { key });
      setInfo((prev) => ({ ...(prev || {}), bonus_total: res.bonus_total }));
      setReadiness((prev) => prev ? {
        ...prev,
        checks: prev.checks.map((c) => c.key === key ? { ...c, claimed: true } : c),
      } : prev);
      triggerBump();
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingKey(null);
    }
  };

  const claimVoteMilestone = async (threshold) => {
    if (pendingKey) return;
    const key = `vote_${threshold}`;
    setPendingKey(key);
    try {
      const res = await apiRequest('POST', '/claim_vote_bonus', { threshold });
      setInfo((prev) => ({
        ...(prev || {}),
        bonus_total: res.bonus_total,
        vote_milestones: res.vote_milestones,
      }));
      triggerBump();
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingKey(null);
    }
  };

  const renderClaimButton = (check) => {
    if (check.claimed) {
      return (
        <Button theme="primary" disabled mb="0" p="2" styles={{ width: 'auto', minWidth: '110px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="check" className="me-1" />
            {t('claimed')}
          </span>
        </Button>
      );
    }
    if (!check.done) {
      return (
        <Button theme="light" disabled mb="0" p="2" styles={{ width: 'auto', minWidth: '110px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="lock" className="me-1" />
            {t('claim')}
          </span>
        </Button>
      );
    }
    return (
      <Button
        theme="primary"
        mb="0"
        p="2"
        styles={{ width: 'auto', minWidth: '110px' }}
        disabled={pendingKey === check.key}
        onClick={() => claim(check.key)}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="gift" className="me-1" />
          {t('claim')}
        </span>
      </Button>
    );
  };

  const totalDisplay = info?.bonus_total || 0;

  return (
    <Base activeNav="home">
      <Back />
      <Title my="2" heading="3" className="mt-4 mb-1">{t('howToEarnBonusPoints')}</Title>

      <Center>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div
            ref={totalRef}
            className={bumping ? 'bonus-total-bump' : ''}
            style={{ fontSize: '3.5rem', fontWeight: 700, lineHeight: 1, color: 'var(--primary-color)', display: 'inline-block' }}
          >
            {totalDisplay}
          </div>
          {floats.map((id) => (
            <span key={id} className="bonus-float-plus" style={{ left: '50%', top: '0' }}>
              +1
            </span>
          ))}
        </div>
      </Center>
      <div className="text-secondary text-center mb-3">
        {t('extraPointsEarned')}
      </div>

      {/* TEAM READINESS */}
      <Card>
        <Title heading="5" my="0" className="mb-1">{t('teamReadiness')}</Title>
        <div className="text-secondary mb-2">{t('readinessOnePointEach')}</div>

        <div>
          {checks.map((check) => (
            <div key={check.key} className="claim-row">
              <span className={`claim-row__icon claim-row__icon--${check.done ? 'done' : 'pending'}`}>
                <Icon name={ICON_BY_KEY[check.key] || 'circle'} />
              </span>
              <span className="claim-row__label">{t(`readinesstext.${check.key}`)}</span>
              {renderClaimButton(check)}
            </div>
          ))}
        </div>

        <Button theme="light" mb="0" styles={{}} p="2" className="px-3 mt-3" onClick={() => navigate('/team-readiness')}>
          <SameLine>
            {t('completeMore')}
            <Icon name="angle-right" className="ms-2" />
          </SameLine>
        </Button>
      </Card>

      {/* VOTE MILESTONES */}
      {info?.voting_active && Array.isArray(info?.vote_milestones) && info.vote_milestones.length > 0 && (
        <Card>
          <Title heading="5" my="0" className="mb-1">{t('voteMilestones') || 'Get Votes'}</Title>
          <div className="text-secondary mb-2">
            {(t('voteMilestonesInfo') || 'Earn 1 bonus point for each vote milestone you reach.')} ({info.vote_count || 0} {t('cheers')})
          </div>
          <div>
            {info.vote_milestones.map((m) => {
              const key = `vote_${m.threshold}`;
              return (
                <div key={m.threshold} className="claim-row">
                  <span className={`claim-row__icon claim-row__icon--${m.claimable || m.claimed ? 'done' : 'pending'}`}>
                    <Icon name="party-horn" />
                  </span>
                  <span className="claim-row__label">
                    {(t('reachVotes') || 'Reach {{n}} votes').replace('{{n}}', m.threshold)}
                  </span>
                  {m.claimed ? (
                    <Button theme="primary" disabled mb="0" p="2" styles={{ width: 'auto', minWidth: '110px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon name="check" className="me-1" />
                        {t('claimed')}
                      </span>
                    </Button>
                  ) : !m.claimable ? (
                    <Button theme="light" disabled mb="0" p="2" styles={{ width: 'auto', minWidth: '110px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon name="lock" className="me-1" />
                        {t('claim')}
                      </span>
                    </Button>
                  ) : (
                    <Button
                      theme="primary"
                      mb="0"
                      p="2"
                      styles={{ width: 'auto', minWidth: '110px' }}
                      disabled={pendingKey === key}
                      onClick={() => claimVoteMilestone(m.threshold)}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon name="gift" className="me-1" />
                        {t('claim')}
                      </span>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          <Button theme="light" mb="0" styles={{}} p="2" className="px-3 mt-3" onClick={() => navigate('/cheers')}>
            <SameLine>
              {t('shareYourTeam')}
              <Icon name="angle-right" className="ms-2" />
            </SameLine>
          </Button>
        </Card>
      )}

      {/* MISSIONS (photo missions + video submissions live on one page) */}
      {(info?.has_missions || info?.has_social_missions) && (
        <Card>
          <Title heading="5" my="0" className="mb-1">{t('missions')}</Title>
          <div className="text-secondary mb-3">{t('missionsBonusInfo')}</div>
          <Button theme="light" mb="0" styles={{}} p="2" className="px-3" onClick={() => navigate('/missions')}>
            <SameLine>
              {t('viewMissions')}
              <Icon name="angle-right" className="ms-2" />
            </SameLine>
          </Button>
        </Card>
      )}

      {/* QUIZZES */}
      {info?.has_quizzes && (
        <Card>
          <Title heading="5" my="0" className="mb-1">{t('quizzes')}</Title>
          <div className="text-secondary mb-3">{t('quizzesBonusInfo')}</div>
          <Button theme="light" mb="0" styles={{}} p="2" className="px-3" onClick={() => navigate('/event-surveys')}>
            <SameLine>
              {t('viewQuizzes')}
              <Icon name="angle-right" className="ms-2" />
            </SameLine>
          </Button>
        </Card>
      )}

      {!!error && (
        <div className="px-3 mt-3 text-danger">
          <small>{t(error)}</small>
        </div>
      )}
    </Base>
  );
};

export default BonusPoints;
